import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Icon, Modal, SpaceBetween } from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { useApi } from '@/src/store/api.context';
import { JAM_EVENT_DETAILS_ROUTES } from '@/src/routes';
import { JamSupportChat } from '@/src/types/JamSupportChat';
import ChatButton from '../../atoms/ChatButton/ChatButton';
import { getRightOffset } from '@/src/utils/chat.utils';
import SupportChatStartModal from '../../molecules/MyJams/SupportChatStartModal/SupportChatStartModal';
import JamChat from '../../molecules/MyJams/JamChat/JamChat';
import './JamChatContainer.scss';
import { ChatMessage } from '@/src/types/Chat';
import { useJamChat } from '@/src/store/jam-chat.context';
import { useJamEventDetails } from '@/src/store/jam-event-details.context';
import { useFlashbars } from '@/src/store/flashbar.context';
import { useCampaigns } from '@/src/store/campaigns.context';

interface JamChatContainerProps {
  isModal?: boolean;
}

const JamChatContainer: React.FC<JamChatContainerProps> = ({ isModal = true }) => {
  const { t } = useTranslation();
  const { event, eventName, eventSlug } = useJamEventDetails();
  const {
    teamChat,
    supportChats,
    activeChatType,
    setActiveChatType,
    unreadTeamChatMessage,
    selectedSupportChatId,
    setSelectedSupportChatId,
    viewAllSupportChats,
    setViewAllSupportChats,
    unreadSupportChatCounter,
    updateSupportChatReadCounter,
    triggerSupportChatSignal,
    setTriggerSupportChatSignal,
  } = useJamChat();
  const { showCampaignRules, campaignStarted } = useCampaigns();
  const { jamMessageApi, jamSupportChatApi } = useApi();
  const { addErrorFlashbar } = useFlashbars();
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [showSupportChatModal, setShowSupportChatModal] = useState(false);

  // This state is used to show chat history after the chat has ended.
  // The `selectedSupportChat` state shows the current active support chat, which may or may not be undefined depending on if one is currently active.
  // The `lastSelectedSupportChat` should always be populated with the last chat support chat the user was in,
  // so that we can continue to show chat history even after the chat has been removed from the chat list (ended).
  const [selectedSupportChat, setSelectedSupportChat] = useState<JamSupportChat | undefined>();
  const [lastSelectedSupportChat, setLastSelectedSupportChat] = useState<JamSupportChat | undefined>();

  // open the support chat modal if
  // user just started the chat
  const openSupportChat = useRef(false);
  const buttonRefs = useRef<Record<string, HTMLDivElement>>({});

  const teamMessages = teamChat?.chatMessages || [];

  const isSupportUser = !!event?.supportUser;
  const hasTeamChatEnabled = event?.teamChatEnabled;
  const isSupportChatActive = activeChatType === 'support';
  const isTeamChatActive = activeChatType === 'team';
  const isModalActive = isTeamChatActive || (!event?.facilitator && isSupportChatActive) || viewAllSupportChats;
  const hideForCampaign =
    event?.type === 'CAMPAIGN_GROUP' && !isSupportUser && !event.ended && (!campaignStarted || showCampaignRules);
  const isChatEnded = isSupportChatActive ? !selectedSupportChat?.active : false;

  useEffect(() => {
    if (selectedSupportChatId) {
      const selectedChat = supportChats.find(({ id }) => id === selectedSupportChatId) as JamSupportChat;
      setSelectedSupportChat(selectedChat);

      if (!!selectedChat) {
        setLastSelectedSupportChat(selectedChat);
      }
    }
  }, [supportChats, selectedSupportChatId]);

  useEffect(() => {
    if (triggerSupportChatSignal) {
      onSupportChatPress(triggerSupportChatSignal);
      setTriggerSupportChatSignal('');
    }
  }, [triggerSupportChatSignal]);

  const chatMessages = useMemo<ChatMessage[]>(() => {
    if (isTeamChatActive) {
      return teamMessages;
    }

    return selectedSupportChat?.messages ?? lastSelectedSupportChat?.messages ?? [];
  }, [teamMessages, activeChatType, selectedSupportChat, lastSelectedSupportChat]);

  const sendTeamChatMessage = async (message: string) => {
    try {
      await jamMessageApi.sendTeamChatMessage({
        eventName,
        message,
        silent: true,
      });
    } catch (e) {
      // error handled in API
    }
  };

  const sendSupportChatMessage = (message: string) => {
    if (supportChats.length) {
      void jamSupportChatApi.sendSupportChatMessage(selectedSupportChat as JamSupportChat, message, true);
    }
  };

  const onCloseWindow = () => {
    setActiveChatType(undefined);
    setSelectedSupportChatId(undefined);
    setLastSelectedSupportChat(undefined);
    setViewAllSupportChats(false);
  };

  const sendMessage = (message: string) => {
    if (isTeamChatActive) {
      void sendTeamChatMessage(message);
    } else {
      void sendSupportChatMessage(message);
    }
  };

  const startSupportChat = async (params: { challengeId: string | null; reason: string }) => {
    try {
      openSupportChat.current = true;
      await jamSupportChatApi.createSupportChat(eventName, params.challengeId, params.reason, true);
    } catch (e) {
      openSupportChat.current = false;
    }
  };

  const endSupportChat = () => {
    if (supportChats.length) {
      setIsEnding(true);
      jamSupportChatApi
        .endSupportChat(selectedSupportChat as JamSupportChat, true)
        .then(() => setShowEndConfirm(false))
        .catch((e) => addErrorFlashbar(e))
        .finally(() => setIsEnding(false));
    }
  };

  const onTeamChatPress = () => {
    setViewAllSupportChats(false);
    setActiveChatType(isTeamChatActive ? undefined : 'team');
  };

  const onSupportChatPress = (id?: string) => {
    if (id) {
      updateSupportChatReadCounter(id, 0);
    }
    if (selectedSupportChatId) {
      if (selectedSupportChatId !== id) {
        setSelectedSupportChatId(id);
        setActiveChatType('support');
        return;
      }
      setActiveChatType(isSupportChatActive ? undefined : 'support');
      return;
    }
    setShowSupportChatModal(true);
  };

  const onSupportChatSelect = (chat: JamSupportChat) => {
    updateSupportChatReadCounter(chat.id, 0);
    setSelectedSupportChatId(chat.id);
    setActiveChatType('support');
  };

  const onSupportChatBackPress = () => {
    setSelectedSupportChatId(undefined);
    setActiveChatType(undefined);
    setLastSelectedSupportChat(undefined);
  };

  const onSupportChatMinimizePress = (id: string) => {
    if (!event?.facilitator) {
      // if the chat is ended by facilitator. remove modal on minimize
      if (isSupportChatActive && !selectedSupportChat?.active) {
        onCloseWindow();
        return;
      }
      onSupportChatPress(id);
    }
    setViewAllSupportChats(false);
  };

  const openChatExternal = () => {
    window.open(
      JAM_EVENT_DETAILS_ROUTES.TeamChat.resolve(eventSlug),
      '_blank',
      'toolbar=0,location=0,menubar=0,noopener,noreferrer'
    );
  };

  const openTeamExternal = () => {
    if (!selectedSupportChat?.creatorTeamName) {
      addErrorFlashbar(t(i18nKeys.jamChat.supportChat.errors.noTeam));
      return;
    }
    window.open(
      JAM_EVENT_DETAILS_ROUTES.TeamInfo.resolve([eventSlug, selectedSupportChat.teamId]),
      '_blank',
      'noopener,noreferrer'
    );
  };

  useEffect(() => {
    // this is for full screen where chat is not a modal
    // and for now we are only supporting full screen for
    // team chat
    if (!isModal) {
      setActiveChatType('team');
    }
  }, [isModal]);

  useEffect(() => {
    if (
      // switch support chat if selected chat is not active anymore
      (selectedSupportChatId && supportChats.find(({ id }) => id === selectedSupportChatId)?.active) ||
      !supportChats.length ||
      event?.facilitator
    ) {
      return;
    }
    setSelectedSupportChatId(supportChats[0].id);
    if (openSupportChat.current) {
      setActiveChatType('support');
      openSupportChat.current = false;
    }
  }, [selectedSupportChatId, supportChats]);

  useEffect(() => {
    // reset all data when you leave the game ui
    return () => {
      setViewAllSupportChats(false);
      setSelectedSupportChatId(undefined);
      setActiveChatType(undefined);
      setLastSelectedSupportChat(undefined);
    };
  }, []);

  const renderEndChatButton = () => {
    return (
      <Button data-testid="end-chat-button" data-classname="danger" onClick={() => setShowEndConfirm(true)}>
        {t(i18nKeys.jamChat.actions.endChat)}
      </Button>
    );
  };

  const renderChatHeader = () => {
    if (isSupportChatActive || viewAllSupportChats) {
      return (
        <div data-testid="support-chat-header" className="chat-header-outer">
          <div className="chat-header support-header">
            <Box variant="h3">{getSupportChatTitle(selectedSupportChat)}</Box>
            <SpaceBetween direction="horizontal" size="xs">
              {selectedSupportChat?.active && !isSupportUser && renderEndChatButton()}
              {!isChatEnded && (
                <button
                  data-testid="minimize-chat-button"
                  aria-label={t(i18nKeys.jamChat.ariaLabels.minimizeChat)}
                  type="button"
                  className="minimize"
                  onClick={() => onSupportChatMinimizePress(selectedSupportChat?.id as string)}
                >
                  <Icon name="subtract-minus" />
                </button>
              )}
            </SpaceBetween>
          </div>
          {isSupportUser && selectedSupportChatId && (
            <div>
              <div className="action-bar">
                <Button
                  data-testid="support-chat-back-button"
                  iconName="angle-left"
                  variant="inline-link"
                  data-classname="back"
                  onClick={onSupportChatBackPress}
                >
                  {t(i18nKeys.general.back)}
                </Button>
                <div className="flexed" />
                <SpaceBetween direction="horizontal" size="xs">
                  {!!selectedSupportChat?.creatorTeamName && (
                    <Button data-classname="info" iconName="external" onClick={openTeamExternal}>
                      {t(i18nKeys.jamChat.titles.teamInfo)}
                    </Button>
                  )}
                  {selectedSupportChat?.active && renderEndChatButton()}
                </SpaceBetween>
              </div>
            </div>
          )}
        </div>
      );
    }
    return (
      <div data-testid="team-chat-header" className="chat-header">
        <Box variant="h3">{t(i18nKeys.myJams.team.teamChat)}</Box>
        {isModal && (
          <SpaceBetween size="xs" direction="horizontal">
            <Button
              data-testid="open-team-chat-external"
              ariaLabel={t(i18nKeys.jamChat.ariaLabels.openTeamChat)}
              variant="icon"
              iconName="external"
              onClick={openChatExternal}
            />
            {!isChatEnded && (
              <button
                aria-label={t(i18nKeys.jamChat.ariaLabels.minimizeChat)}
                type="button"
                className="minimize"
                onClick={onTeamChatPress}
              >
                <Icon name="subtract-minus" />
              </button>
            )}
          </SpaceBetween>
        )}
      </div>
    );
  };

  const getChatOuterOffset = (): Record<string, string> => {
    if ((isTeamChatActive && !viewAllSupportChats) || !buttonRefs.current.supportChats) {
      return {};
    }

    return getRightOffset(buttonRefs.current.supportChats);
  };

  const renderChatModal = () => {
    if (viewAllSupportChats && !selectedSupportChatId && event?.facilitator) {
      return (
        <div className="chat-outer" style={getChatOuterOffset()}>
          {renderChatHeader()}
          {!!supportChats.length &&
            supportChats.map((supportChat) => (
              <button
                className="supportchat-item"
                onClick={(e) => {
                  e.preventDefault();
                  onSupportChatSelect(supportChat);
                }}
                key={supportChat.id}
              >
                {!!unreadSupportChatCounter[supportChat.id] && (
                  <span className="counter">{unreadSupportChatCounter[supportChat.id] || 0}</span>
                )}
                <span className="chat-title">{supportChat.creatorDisplayName}</span>
                <Icon name="angle-right" />
              </button>
            ))}
          {!supportChats.length && (
            <Box variant="p" textAlign="center" margin="xxxl" padding="xxxl" color="text-status-info">
              {t(i18nKeys.jamChat.status.noActiveSupportChats)}
            </Box>
          )}
        </div>
      );
    }

    return (
      <div className="chat-outer" style={getChatOuterOffset()}>
        {renderChatHeader()}
        <Box textAlign="center" variant="p" margin="xxs" color="text-status-info">
          {t(i18nKeys.general.avoidPIIWarning)}
        </Box>
        <div className="chat" key={`${activeChatType}-${selectedSupportChatId}`}>
          <JamChat
            disabled={isChatEnded}
            ended={isChatEnded}
            messages={chatMessages}
            sendMessage={sendMessage}
            closeWindow={onCloseWindow}
          />
        </div>
      </div>
    );
  };

  const getSupportChatTitle = (chat?: JamSupportChat) => {
    if (!chat) {
      if (viewAllSupportChats) {
        return t(i18nKeys.jamChat.supportChat.supportChats);
      }
      return t(i18nKeys.jamChat.supportChat.title);
    }
    return `${t(i18nKeys.jamChat.supportChat.title)} ${isSupportUser ? ` - ${chat.creatorDisplayName}` : ''}`;
  };

  const renderSupportChatButtons = () => {
    if (!event?.supportChatEnabled) {
      return null;
    }

    if (!supportChats.length && !event.facilitator) {
      return (
        <ChatButton
          onClick={() => onSupportChatPress()}
          chatType="support"
          title={t(i18nKeys.jamChat.supportChat.title)}
          counter={0}
          isActive={false}
          key={'Support-Chat'}
          id="supportChatButton"
        />
      );
    }

    if (event.facilitator && supportChats.length) {
      return (
        <ChatButton
          onClick={() => {
            setViewAllSupportChats((prev) => !prev);
          }}
          chatType="support"
          title={t(i18nKeys.jamChat.supportChat.supportChats)}
          counter={Object.values(unreadSupportChatCounter).filter((count) => count).length}
          isActive={viewAllSupportChats}
          key={'Support-Chats'}
          ref={(ref: HTMLDivElement) => (buttonRefs.current.supportChats = ref)}
          id="supportChatButton"
        />
      );
    }

    return supportChats.map((chat, index) => (
      <ChatButton
        onClick={() => onSupportChatPress(chat.id)}
        chatType="support"
        title={getSupportChatTitle(chat)}
        counter={unreadSupportChatCounter[chat.id] || 0}
        isActive={isSupportChatActive && selectedSupportChatId === chat.id}
        key={chat.id}
        ref={(ref: HTMLDivElement) => (buttonRefs.current.supportChats = ref)}
        id={`supportChatButton-${index}`}
      />
    ));
  };

  const renderChatButton = () => (
    <div className="chat-buttons">
      <SpaceBetween size="xs" direction="horizontal">
        {renderSupportChatButtons()}
        {hasTeamChatEnabled && (
          <ChatButton
            onClick={onTeamChatPress}
            chatType="team"
            title={t(i18nKeys.myJams.team.teamChat)}
            counter={unreadTeamChatMessage}
            isActive={isTeamChatActive}
            id="teamChatButton"
          />
        )}
      </SpaceBetween>
    </div>
  );

  return (
    <div data-testid="jam-chat-container" className={`jam-chat-container ${isModal ? 'chat-modal' : 'fullscreen'}`}>
      {isModalActive && renderChatModal()}
      {!hideForCampaign && isModal && renderChatButton()}
      {showSupportChatModal && (
        <SupportChatStartModal
          handleClose={() => setShowSupportChatModal(false)}
          challengeOptions={
            (event?.challenges || []).map((challenge) => ({
              value: challenge.id,
              label: challenge?.title || challenge.id,
            })) || []
          }
          onStartSupportChat={startSupportChat}
        />
      )}

      <Modal
        data-testid="end-chat-confirm-modal"
        visible={showEndConfirm}
        header={t(i18nKeys.jamChat.actions.endChatConfirm)}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                data-testid="end-chat-cancel-button"
                disabled={isEnding}
                variant="link"
                onClick={() => setShowEndConfirm(false)}
              >
                {t(i18nKeys.general.cancel)}
              </Button>

              <Button
                data-testid="end-chat-confirm-button"
                loading={isEnding}
                variant="primary"
                onClick={endSupportChat}
              >
                {t(i18nKeys.jamChat.actions.endChat)}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {t(i18nKeys.jamChat.actions.areYouSureEnd)}
      </Modal>
    </div>
  );
};

export default JamChatContainer;
